import { supabase } from './supabase'
import {
  chunk,
  normaliseTag,
  partialMessage,
  storedVariants,
  type TagOutcome,
} from './tagging'

/**
 * The two writes. Everything decidable without a database lives in
 * `tagging.ts`, so this file holds only what genuinely has to talk to Supabase
 * — which is also why the tagging tests run under plain node.
 *
 * **Both functions read before they write, and report what the database
 * actually did.** That costs one extra round trip and it is not optional:
 *
 * - `TagOutcome.changed` is what Undo runs over. An over-estimate there means
 *   an undo deleting rows a human created. Two tabs open, one of them holding
 *   a tag map from an hour ago, is enough to produce it: the second tab thinks
 *   386 agents need the tag, `ON CONFLICT DO NOTHING` writes nothing at all,
 *   and an Undo of that no-op would delete all 386 rows the first tab wrote.
 * - Removal has to delete the strings the rows are actually stored under, not
 *   the normalised one, or a row saved as `VIP` survives a delete of `vip`
 *   while the count on the button said it would go.
 *
 * The read answers both questions at once, from the database rather than from
 * a client-side map that may be stale.
 */

type Row = { agent_id: string; tag: string }

/**
 * Which of these agents currently hold this tag, and under what spellings.
 * Chunked for the same URL-length reason every other query here is.
 */
async function currentlyTagged(
  agentIds: readonly string[],
  tag: string,
): Promise<{ holders: Set<string>; variants: Set<string> }> {
  const holders = new Set<string>()
  const variants = new Set<string>()
  for (const batch of chunk(agentIds)) {
    const { data, error } = await supabase
      .from('agent_tags')
      .select('agent_id,tag')
      .in('agent_id', batch)
    if (error) throw new Error(error.message)
    for (const row of (data ?? []) as Row[]) {
      if (normaliseTag(row.tag) !== tag) continue
      holders.add(row.agent_id)
      variants.add(row.tag)
    }
  }
  return { holders, variants }
}

/**
 * Add a tag to agents who do not already have it.
 *
 * `changed` is the set this call genuinely inserted — computed against a fresh
 * read, and cross-checked against what PostgREST returns where it returns
 * anything. `ignoreDuplicates` still guards the millisecond between the read
 * and the write, and a row it silently absorbs is correctly *excluded* from
 * `changed` because the read already saw it.
 */
export async function addTag(
  agentIds: readonly string[],
  rawTag: string,
  createdBy?: string | null,
): Promise<TagOutcome> {
  const tag = normaliseTag(rawTag)
  const base = { action: 'add' as const, tag, attempted: agentIds.length }
  if (!tag || agentIds.length === 0) return { ...base, changed: [], error: null }

  let holders: Set<string>
  try {
    ;({ holders } = await currentlyTagged(agentIds, tag))
  } catch (err) {
    return {
      ...base,
      changed: [],
      error: partialMessage('add', 0, agentIds.length, (err as Error).message),
    }
  }

  const needed = agentIds.filter((id) => !holders.has(id))
  const changed: string[] = []

  for (const batch of chunk(needed)) {
    const rows = batch.map((agent_id) => ({
      agent_id,
      tag,
      ...(createdBy ? { created_by: createdBy } : {}),
    }))
    const { data, error } = await supabase
      .from('agent_tags')
      .upsert(rows, { onConflict: 'agent_id,tag', ignoreDuplicates: true })
      .select('agent_id')
    if (error) {
      return {
        ...base,
        changed,
        error: partialMessage('add', changed.length, agentIds.length, error.message),
      }
    }
    // Prefer what came back. `ON CONFLICT DO NOTHING ... RETURNING` yields only
    // the rows that were really inserted, which is the strongest answer
    // available. When the client returns nothing to `select` — a behaviour that
    // has changed across versions — fall back to the batch, which the fresh
    // read above already narrowed to agents who did not have the tag.
    const returned = (data ?? null) as { agent_id: string }[] | null
    changed.push(...(returned ? returned.map((r) => r.agent_id) : batch))
  }

  return { ...base, changed, error: null }
}

/**
 * Remove a tag from agents who have it.
 *
 * Deletes every stored spelling that normalises to this tag, so a row saved as
 * `VIP` goes when `vip` is removed — matching what the count on the button
 * promised.
 */
export async function removeTag(
  agentIds: readonly string[],
  rawTag: string,
  tagsByAgent?: Map<string, string[]>,
): Promise<TagOutcome> {
  const tag = normaliseTag(rawTag)
  const base = { action: 'remove' as const, tag, attempted: agentIds.length }
  if (!tag || agentIds.length === 0) return { ...base, changed: [], error: null }

  let holders: Set<string>
  let variants: Set<string>
  try {
    ;({ holders, variants } = await currentlyTagged(agentIds, tag))
  } catch (err) {
    return {
      ...base,
      changed: [],
      error: partialMessage('remove', 0, agentIds.length, (err as Error).message),
    }
  }

  // The client's own map is folded in so a spelling that exists on an agent
  // outside this selection is still deleted from the ones inside it.
  for (const v of tagsByAgent ? storedVariants(tagsByAgent, tag) : [tag]) variants.add(v)
  variants.add(tag)

  const holding = agentIds.filter((id) => holders.has(id))
  const spellings = [...variants]
  const changed: string[] = []

  for (const batch of chunk(holding)) {
    const { data, error } = await supabase
      .from('agent_tags')
      .delete()
      .in('tag', spellings)
      .in('agent_id', batch)
      .select('agent_id')
    if (error) {
      return {
        ...base,
        changed,
        error: partialMessage('remove', changed.length, agentIds.length, error.message),
      }
    }
    const returned = (data ?? null) as { agent_id: string }[] | null
    changed.push(...(returned ? [...new Set(returned.map((r) => r.agent_id))] : batch))
  }

  return { ...base, changed, error: null }
}
